import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import './rednote-app-launcher.js';
import { useLightbox } from '../components/Lightbox';
import ScanImages from '../components/ScanImages';

const API_BASE = process.env.NODE_ENV === 'development'
  ? (process.env.REACT_APP_API_URL || 'http://localhost:4001')
  : '';

export default function ArticlePage() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [activeImg, setActiveImg] = useState(0);
  // 内容只作参考写法，用户改完再发；发出去的永远是这里编辑后的版本
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [images, setImages] = useState([]);
  const lb = useLightbox();

  useEffect(() => {
    if (!id) return;
    fetch(`${API_BASE}/api/scan/content/${id}`)
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(data.error);
        setArticle(data);
        setTitle(data.title || '');
        setContent(data.content || '');
        setImages((data.images || []).filter(Boolean));
      })
      .catch(() => setStatus('内容加载失败，请重新扫码'))
      .finally(() => setLoading(false));
  }, [id]);

  const handlePublish = () => {
    if (!article) return;
    const launcher = window.RednoteAppLauncher;
    if (!launcher) { setStatus('页面加载异常，请刷新重试'); return; }

    if (!content.trim()) { setStatus('请先写下你自己的体验再发布'); return; }

    const pubImages = images.filter((u) => /^https?:\/\//i.test(u));
    if (pubImages.length === 0) {
      setStatus('请先加一张图片再发布');
      return;
    }

    const shareInfo = {
      type: 'normal',
      title: title.trim(),
      content: content.trim(),
      images: pubImages,
    };

    const result = launcher.openPublish(shareInfo);
    if (!result.ok) {
      if (result.reason === 'in_app_browser') {
        setStatus('请点击右上角菜单，在系统浏览器中打开后再发布');
      } else if (result.reason === 'unsupported') {
        setStatus('当前环境不支持发布，请在手机系统浏览器中打开本页面');
      }
    }
  };

  if (loading) {
    return (
      <div style={styles.loadingPage}>
        <div style={styles.loadingLogo}>
          <img src="/favicon.png" alt="logo" style={styles.logoImg} />
        </div>
        <div style={styles.loadingDots}>
          <span style={{ ...styles.dot, animationDelay: '0s' }} />
          <span style={{ ...styles.dot, animationDelay: '0.2s' }} />
          <span style={{ ...styles.dot, animationDelay: '0.4s' }} />
        </div>
        <style>{dotAnim}</style>
      </div>
    );
  }

  if (!article) {
    return (
      <div style={styles.errorPage}>
        <div style={styles.errorIcon}>!</div>
        <p style={styles.errorText}>{status || '内容不存在'}</p>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* top bar */}
      <div style={styles.topBar}>
        <img src="/favicon.png" alt="券宝宝" style={styles.topLogo} />
        <span style={styles.topBrand}>券宝宝</span>
      </div>

      <div style={styles.scrollArea}>
        {/* image carousel */}
        <div style={styles.imageSection}>
          {images.length > 0 && (
            <div style={styles.mainImage}>
              <img
                src={images[Math.min(activeImg, images.length - 1)]}
                alt=""
                style={styles.mainImg}
                onClick={() => lb.open(images, Math.min(activeImg, images.length - 1))}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              {images.length > 1 && (
                <div style={styles.dotsRow}>
                  {images.map((_, i) => (
                    <span
                      key={i}
                      style={{ ...styles.imgDot, ...(i === activeImg ? styles.imgDotActive : {}) }}
                      onClick={() => setActiveImg(i)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          <ScanImages
            articleId={id}
            images={images}
            onChange={(next) => { setImages(next); setActiveImg((i) => Math.min(i, Math.max(0, next.length - 1))); }}
            onOpen={(list, i) => setActiveImg(i)}
            hint="上面的图也只是参考，建议换成你自己拍的照片"
          />
        </div>

        {/* content card：只是参考写法，鼓励用户改成自己的话 */}
        <div style={styles.contentCard}>
          <div style={styles.exampleRow}>
            <span style={styles.exampleTag}>参考写法</span>
            <span style={styles.exampleHint}>下面只是一个参考，建议你自己发挥，写成你自己的真实感受再发布</span>
          </div>
          <input
            style={styles.titleInput}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="写个标题……"
          />
          <div style={styles.divider} />
          <textarea
            style={styles.contentInput}
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="写下你自己的体验……"
            rows={10}
          />
        </div>

        {/* tip */}
        <div style={styles.tipBox}>
          <span style={styles.tipIcon}>i</span>
          <span style={styles.tipText}>
            点发布会跳转小红书，把你改好的标题、正文和图片带过去。如果你是参加活动来分享的，记得在文里提一句。
          </span>
        </div>


        {/* 扫码的人本身就在发小红书，顺手招募：这是最便宜也最合规的博主来源 */}
        <div style={styles.recruitBox} onClick={() => { window.location.hash = '/blogger/enroll'; }}>
          <div style={styles.recruitTitle}>想接商家的探店邀请？</div>
          <div style={styles.recruitSub}>免费吃喝 + 商家给套餐，助理会帮你约时间。点这里登记</div>
        </div>

        {status && (
          <div style={styles.statusBox}>
            <span style={styles.statusText}>{status}</span>
          </div>
        )}

        <div style={{ height: 100 }} />
      </div>

      {/* sticky bottom button */}
      <div style={styles.bottomBar}>
        <button style={styles.publishBtn} onClick={handlePublish}>
          发布到小红书
        </button>
      </div>

      {lb.node}
    </div>
  );
}

const dotAnim = `
@keyframes bounce { 0%,80%,100%{transform:translateY(0)} 40%{transform:translateY(-10px)} }
`;

const styles = {
  page: {
    minHeight: '100vh', background: '#f7f7f8',
    display: 'flex', flexDirection: 'column', maxWidth: 480, margin: '0 auto',
    position: 'relative',
  },
  loadingPage: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', background: '#fff', gap: 24,
  },
  loadingLogo: { opacity: 0.9 },
  logoImg: { width: 72, height: 72, borderRadius: 18 },
  loadingDots: { display: 'flex', gap: 8 },
  dot: {
    width: 8, height: 8, borderRadius: '50%', background: '#FA1C51',
    display: 'inline-block', animation: 'bounce 1.2s infinite ease-in-out',
  },
  errorPage: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', background: '#fff', padding: 32,
  },
  errorIcon: {
    width: 56, height: 56, borderRadius: '50%', background: '#fee2e2',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 28, fontWeight: 700, color: '#FA1C51', marginBottom: 16,
  },
  errorText: { fontSize: 15, color: '#666', textAlign: 'center', lineHeight: 1.6, margin: 0 },

  topBar: {
    display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px',
    background: '#fff', borderBottom: '1px solid #f0f0f0',
    position: 'sticky', top: 0, zIndex: 10,
  },
  topLogo: { width: 28, height: 28, borderRadius: 7 },
  topBrand: { fontSize: 15, fontWeight: 700, color: '#111' },

  scrollArea: { flex: 1, overflowY: 'auto' },

  imageSection: { background: '#fff', marginBottom: 10 },
  mainImage: { position: 'relative', width: '100%', paddingTop: '75%', overflow: 'hidden', background: '#f5f5f5' },
  mainImg: {
    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
    objectFit: 'cover',
  },
  dotsRow: {
    position: 'absolute', bottom: 10, left: 0, right: 0,
    display: 'flex', justifyContent: 'center', gap: 5,
  },
  imgDot: {
    width: 6, height: 6, borderRadius: '50%', background: 'rgba(255,255,255,0.55)',
    cursor: 'pointer', transition: 'background 0.2s',
  },
  imgDotActive: { background: '#fff', width: 18, borderRadius: 3 },
  thumbRow: {
    display: 'flex', gap: 6, padding: '8px 12px', overflowX: 'auto',
  },
  thumb: {
    width: 56, height: 56, objectFit: 'cover', borderRadius: 6,
    flexShrink: 0, cursor: 'pointer', border: '2px solid transparent',
    opacity: 0.7,
  },
  thumbActive: { border: '2px solid #FA1C51', opacity: 1 },

  contentCard: {
    background: '#fff', margin: '0 0 10px', padding: '20px 16px',
  },
  title: { fontSize: 20, fontWeight: 700, color: '#111', margin: '0 0 14px', lineHeight: 1.4 },
  exampleRow: { marginBottom: 12 },
  exampleTag: {
    display: 'inline-block', fontSize: 11, fontWeight: 600, color: '#FA1C51',
    background: '#fff1f4', borderRadius: 4, padding: '2px 7px', marginRight: 6,
  },
  exampleHint: { fontSize: 12, color: '#888', lineHeight: 1.6 },
  titleInput: {
    width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
    fontSize: 19, fontWeight: 700, color: '#111', lineHeight: 1.4,
    padding: 0, margin: '0 0 14px', background: 'transparent', fontFamily: 'inherit',
  },
  contentInput: {
    width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none',
    fontSize: 15, color: '#333', lineHeight: 1.85, padding: 0,
    background: 'transparent', resize: 'vertical', fontFamily: 'inherit',
  },
  divider: { height: 1, background: '#f0f0f0', margin: '0 0 14px' },
  content: { fontSize: 15, color: '#333', lineHeight: 1.85, margin: 0, whiteSpace: 'pre-wrap' },

  recruitBox: {
    background: '#fff', border: '1px solid #ffe4e9', borderRadius: 10,
    margin: '0 12px 12px', padding: '12px 14px', cursor: 'pointer',
  },
  recruitTitle: { fontSize: 14, fontWeight: 600, color: '#FA1C51', marginBottom: 3 },
  recruitSub: { fontSize: 12, color: '#9ca3af', lineHeight: 1.6 },
  tipBox: {
    display: 'flex', alignItems: 'flex-start', gap: 8,
    background: '#fff7f8', borderRadius: 10, margin: '0 12px 10px', padding: '12px 14px',
  },
  tipIcon: {
    width: 16, height: 16, borderRadius: '50%', background: '#FA1C51', color: '#fff',
    fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, marginTop: 1,
  },
  tipText: { fontSize: 13, color: '#888', lineHeight: 1.5 },

  statusBox: {
    margin: '0 12px 8px', background: '#fffbeb', borderRadius: 8,
    padding: '10px 14px', borderLeft: '3px solid #f59e0b',
  },
  statusText: { fontSize: 13, color: '#92400e' },

  bottomBar: {
    position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
    width: '100%', maxWidth: 480, padding: '12px 16px 24px',
    background: '#fff', borderTop: '1px solid #f0f0f0',
    boxSizing: 'border-box',
  },
  publishBtn: {
    width: '100%', padding: '16px', background: '#FA1C51', color: '#fff',
    border: 'none', borderRadius: 14, fontSize: 17, fontWeight: 700, cursor: 'pointer',
    letterSpacing: 1,
  },
};
